import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Building2, Plus, FlaskConical, Clock, CheckCircle, Truck, 
  Activity, LogOut, FileText, Search, RefreshCw, Eye, Calendar, Wallet, FileBarChart2,
  User, Phone, Barcode, Loader2, X, AlertCircle, Printer, Package
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { contractorLabsAPI, labTestsAPI, publicAPI } from '../../services/api';
import toast from 'react-hot-toast';
import JsBarcode from 'jsbarcode';

const ContractorDashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [tenantBranding, setTenantBranding] = useState(null);
  const tenantLogo = tenantBranding?.branding?.logoUrl || '/listech-logo.svg';
  const tenantName = tenantBranding?.nameAr || tenantBranding?.name || 'LIS Techs';
  const [searchQuery, setSearchQuery] = useState('');
  
  // New Order Modal
  const [showNewOrderModal, setShowNewOrderModal] = useState(false);
  const [tests, setTests] = useState([]);
  const [loadingTests, setLoadingTests] = useState(false);
  const [selectedTests, setSelectedTests] = useState([]);
  const [testSearchQuery, setTestSearchQuery] = useState('');
  const [orderForm, setOrderForm] = useState({
    patient_name: '',
    patient_national_id: '',
    patient_dob: '',
    patient_gender: 'Male',
    patient_phone: '',
    notes: ''
  });
  const [submitting, setSubmitting] = useState(false);

  // View Booking Modal
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);

  // New Order Success Modal (with barcodes)
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [createdBooking, setCreatedBooking] = useState(null);

  // Update Status
  const [updatingStatus, setUpdatingStatus] = useState(false);

  useEffect(() => {
    const userData = localStorage.getItem('contractorUser');
    const token = localStorage.getItem('contractorToken');

    if (!token) {
      navigate('/contractor/login');
      return;
    }

    if (userData) {
      const parsed = JSON.parse(userData);
      setUser(parsed);
      // Load the PARENT lab's branding (the lab that created this contractor account)
      // based on the logged-in email. Falls back to the platform logo if no match.
      if (parsed.email) {
        publicAPI.getContractorBranding(parsed.email)
          .then(({ data }) => {
            if (data?.success && data?.data?.branding) setTenantBranding(data.data);
          })
          .catch(() => { /* fall back to platform logo */ });
      }
    }

    fetchBookings();
    fetchTests();
  }, [navigate]);

  const fetchBookings = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (statusFilter) params.status = statusFilter;
      
      const response = await contractorLabsAPI.getMyBookings(params);
      const data = response.data?.data?.items || response.data?.items || [];
      setBookings(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching bookings:', err);
      if (err.response?.status === 401) {
        handleLogout();
      }
    }
    setLoading(false);
  }, [statusFilter]);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  const fetchTests = async () => {
    setLoadingTests(true);
    try {
      const response = await labTestsAPI.getAll({ limit: 500, active: true });
      const data = response.data?.data?.items || response.data?.items || [];
      // Filter tests that have contracted_price > 0
      const testsWithContractedPrice = (Array.isArray(data) ? data : []).filter(
        test => test.contracted_price && test.contracted_price > 0
      );
      setTests(testsWithContractedPrice);
    } catch (err) {
      console.error('Error fetching tests:', err);
    }
    setLoadingTests(false);
  };

  const handleLogout = () => {
    localStorage.removeItem('contractorToken');
    localStorage.removeItem('contractorUser');
    navigate('/contractor/login');
  };

  const resetOrderForm = () => {
    setOrderForm({
      patient_name: '',
      patient_national_id: '',
      patient_dob: '',
      patient_gender: 'Male',
      patient_phone: '',
      notes: ''
    });
    setSelectedTests([]);
    setTestSearchQuery('');
  };

  const toggleTest = (test) => {
    setSelectedTests(prev => {
      const exists = prev.find(t => t._id === test._id);
      if (exists) {
        return prev.filter(t => t._id !== test._id);
      }
      // Always use contracted_price for contractor labs
      return [...prev, { ...test, price: test.contracted_price }];
    });
  };

  const handleSubmitOrder = async () => {
    // Validation
    if (!orderForm.patient_name || !orderForm.patient_national_id || !orderForm.patient_dob || !orderForm.patient_gender) {
      toast.error('يرجى ملء جميع بيانات المريض المطلوبة');
      return;
    }
    if (selectedTests.length === 0) {
      toast.error('يرجى اختيار تحليل واحد على الأقل');
      return;
    }

    setSubmitting(true);
    try {
      const data = {
        ...orderForm,
        contractor_lab_id: user?.contractor_lab_id || user?.branch_id,
        tests: selectedTests.map(t => ({
          lab_test_id: t._id,
          price: t.contracted_price
        }))
      };

      const response = await contractorLabsAPI.createBooking(data);
      const newBooking = response.data?.data?.contractorBooking || response.data?.data;
      
      setShowNewOrderModal(false);
      resetOrderForm();
      
      // Show success modal with barcodes
      setCreatedBooking(newBooking);
      setShowSuccessModal(true);
      
      fetchBookings();
      toast.success('تم إنشاء الطلب بنجاح');
    } catch (err) {
      toast.error(err.response?.data?.message || 'حدث خطأ أثناء إنشاء الطلب');
    }
    setSubmitting(false);
  };

  const handleUpdateStatus = async (bookingId, newStatus) => {
    setUpdatingStatus(true);
    try {
      await contractorLabsAPI.updateBookingStatus(bookingId, { status: newStatus });
      toast.success('تم تحديث الحالة بنجاح');
      fetchBookings();
      
      // Update selected booking if viewing
      if (selectedBooking?._id === bookingId) {
        setSelectedBooking(prev => ({ ...prev, status: newStatus }));
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'حدث خطأ أثناء تحديث الحالة');
    }
    setUpdatingStatus(false);
  };

  const getStatusInfo = (status) => {
    const statusMap = {
      pending: { label: 'قيد الانتظار', color: 'bg-amber-100 text-amber-700', icon: Clock },
      collected: { label: 'تم الجمع', color: 'bg-blue-100 text-blue-700', icon: CheckCircle },
      in_transit: { label: 'في التوصيل', color: 'bg-purple-100 text-purple-700', icon: Truck },
      processing: { label: 'قيد المعالجة', color: 'bg-cyan-100 text-cyan-700', icon: Activity },
      completed: { label: 'مكتمل', color: 'bg-green-100 text-green-700', icon: CheckCircle },
      cancelled: { label: 'ملغي', color: 'bg-red-100 text-red-700', icon: X }
    };
    return statusMap[status] || statusMap.pending;
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('ar-SA-u-ca-gregory', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatTime = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleTimeString('ar-SA-u-ca-gregory', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredBookings = bookings.filter(b => 
    b.patient_name?.includes(searchQuery) ||
    b.patient_national_id?.includes(searchQuery) ||
    b.booking_no?.includes(searchQuery)
  );

  const filteredTests = tests.filter(t =>
    t.name_en?.toLowerCase().includes(testSearchQuery.toLowerCase()) ||
    t.name_ar?.includes(testSearchQuery)
  );

  const totalAmount = selectedTests.reduce((sum, t) => sum + (t.contracted_price || 0), 0);

  // Stats
  const stats = {
    total: bookings.length,
    pending: bookings.filter(b => b.status === 'pending').length,
    inProgress: bookings.filter(b => ['collected', 'in_transit', 'processing'].includes(b.status)).length,
    completed: bookings.filter(b => b.status === 'completed').length
  };

  // Print barcodes
  const handlePrintBarcodes = (booking) => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.error('يرجى السماح بالنوافذ المنبثقة للطباعة');
      return;
    }

    const barcodeHtml = booking.tests?.map((test, idx) => `
      <div style="page-break-inside: avoid; margin-bottom: 20px; padding: 15px; border: 1px solid #ddd; border-radius: 8px;">
        <div style="text-align: center; margin-bottom: 10px;">
          <svg id="barcode-${idx}"></svg>
        </div>
        <div style="text-align: center; font-size: 14px;">
          <strong>${test.lab_test_id?.name_en || 'Test'}</strong>
          ${test.lab_test_id?.name_ar ? `<br><span style="color: #666;">${test.lab_test_id.name_ar}</span>` : ''}
        </div>
        <div style="text-align: center; font-size: 12px; color: #888; margin-top: 5px;">
          المريض: ${booking.patient_name} | الهوية: ${booking.patient_national_id}
        </div>
      </div>
    `).join('');

    printWindow.document.write(`
      <!DOCTYPE html>
      <html dir="rtl">
      <head>
        <title>طباعة الباركودات - طلب #${booking.booking_no}</title>
        <script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.5/dist/JsBarcode.all.min.js"></script>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #333; padding-bottom: 15px; }
          .header h1 { margin: 0; font-size: 18px; }
          .header p { margin: 5px 0; color: #666; font-size: 14px; }
          @media print {
            .no-print { display: none; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>${user?.contractor_lab_name || 'المختبر'}</h1>
          <p>طلب رقم: #${booking.booking_no}</p>
          <p>التاريخ: ${formatDate(booking.created_at)}</p>
        </div>
        <div class="no-print" style="text-align: center; margin-bottom: 20px;">
          <button onclick="window.print()" style="padding: 10px 30px; font-size: 16px; cursor: pointer; background: #2563eb; color: white; border: none; border-radius: 8px;">
            🖨️ طباعة
          </button>
        </div>
        ${barcodeHtml}
        <script>
          ${booking.tests?.map((test, idx) => `
            JsBarcode("#barcode-${idx}", "${test.barcode}", {
              format: "CODE128",
              width: 2,
              height: 60,
              displayValue: true,
              fontSize: 14,
              margin: 10
            });
          `).join('')}
        </script>
      </body>
      </html>
    `);
    printWindow.document.close();
  };

  if (loading && bookings.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-3" />
          <p className="text-gray-500 text-sm">جاري تحميل البيانات...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100" dir="rtl">
      {/* Header - Same style as Patient Portal */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-3">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <img src={tenantLogo} alt={tenantName} className="h-10 w-auto object-contain flex-shrink-0" />
              <div className="border-r border-gray-200 pr-3 min-w-0">
                <h1 className="text-base font-bold text-slate-800 truncate">{tenantName}</h1>
                <p className="text-blue-600 text-xs">بوابة المختبرات المتعاقدة</p>
              </div>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0">
              <div className="text-left hidden sm:block">
                <p className="font-medium text-slate-800 text-sm">{user?.name}</p>
                <p className="text-slate-500 text-xs">{user?.contractor_lab_name || user?.email}</p>
              </div>
              <button
                onClick={() => navigate('/contractor/statement')}
                className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 rounded-lg text-sm text-slate-700 hover:bg-gray-50 transition-all"
                title="كشف الحساب"
              >
                <Wallet className="w-4 h-4" />
                <span className="hidden sm:inline">كشف الحساب</span>
              </button>
              <button
                onClick={handleLogout}
                className="p-2 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                title="تسجيل الخروج"
              >
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 md:px-6 py-5">
        {/* Welcome + Stats Row */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-5">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
              <Building2 className="w-6 h-6 text-blue-600" />
            </div>
            <div className="min-w-0">
              <h2 className="text-xl font-bold text-slate-800 truncate">مرحباً، {user?.name}</h2>
              <p className="text-slate-500 text-sm">يمكنك إدارة طلبات التحاليل ومتابعة حالتها</p>
            </div>
          </div>

          {/* Stats */}
          <div className="flex items-center gap-2 md:gap-3 overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0">
            <div className="bg-white rounded-lg px-4 py-2.5 border border-gray-200 flex items-center gap-2">
              <FileText className="w-4 h-4 text-blue-600" />
              <span className="text-xl font-bold text-slate-800">{stats.total}</span>
              <span className="text-sm text-slate-500">طلب</span>
            </div>
            <div className="bg-white rounded-lg px-4 py-2.5 border border-gray-200 flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-600" />
              <span className="text-xl font-bold text-slate-800">{stats.completed}</span>
              <span className="text-sm text-slate-500">مكتمل</span>
            </div>
            <div className="bg-white rounded-lg px-4 py-2.5 border border-gray-200 flex items-center gap-2">
              <Clock className="w-4 h-4 text-amber-600" />
              <span className="text-xl font-bold text-slate-800">{stats.pending + stats.inProgress}</span>
              <span className="text-sm text-slate-500">قيد المعالجة</span>
            </div>
          </div>
        </div>

        {/* Actions & Filters */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 mb-5">
          <div className="flex flex-col md:flex-row gap-3 items-center justify-between">
            <div className="flex flex-1 gap-3 w-full md:w-auto">
              <div className="relative flex-1">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="البحث برقم الطلب أو اسم المريض..."
                  className="w-full px-4 py-2.5 pr-11 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all"
                />
                <Search className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all"
              >
                <option value="">جميع الحالات</option>
                <option value="pending">قيد الانتظار</option>
                <option value="collected">تم الجمع</option>
                <option value="in_transit">في التوصيل</option>
                <option value="processing">قيد المعالجة</option>
                <option value="completed">مكتمل</option>
              </select>
              <button
                onClick={fetchBookings}
                className="p-2.5 text-gray-600 hover:bg-gray-100 rounded-xl border border-gray-200 transition-all"
              >
                <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>
            <button
              onClick={() => setShowNewOrderModal(true)}
              className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-all"
            >
              <Plus className="w-5 h-5" />
              طلب تحليل جديد
            </button>
          </div>
        </div>

        {/* Bookings List */}
        <div className="bg-white rounded-xl border border-gray-200">
          <div className="px-5 py-3 border-b border-gray-200 flex items-center gap-2">
            <FlaskConical className="w-5 h-5 text-blue-600" />
            <h3 className="font-bold text-slate-800">سجل الطلبات</h3>
          </div>

          {filteredBookings.length === 0 ? (
            <div className="p-12 text-center">
              <FlaskConical className="w-14 h-14 text-gray-300 mx-auto mb-3" />
              <p className="text-slate-500 mb-4">لا توجد طلبات</p>
              <button
                onClick={() => setShowNewOrderModal(true)}
                className="px-5 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-all"
              >
                إنشاء طلب جديد
              </button>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {filteredBookings.map((booking) => {
                const status = getStatusInfo(booking.status);
                const StatusIcon = status.icon;
                
                return (
                  <div
                    key={booking._id}
                    className="px-4 md:px-5 py-3.5 hover:bg-gray-50 transition-colors flex flex-col md:flex-row md:items-center md:justify-between gap-3"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                        <FlaskConical className="w-5 h-5 text-blue-600" />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                          <span className="font-bold text-slate-800">#{booking.booking_no}</span>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium flex items-center gap-1 ${status.color}`}>
                            <StatusIcon className="w-3 h-3" />
                            {status.label}
                          </span>
                        </div>
                        <div className="flex items-center gap-3 text-xs text-slate-500 flex-wrap">
                          <span className="flex items-center gap-1 truncate">
                            <User className="w-3.5 h-3.5" />
                            {booking.patient_name}
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3.5 h-3.5" />
                            {formatDate(booking.created_at)}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3.5 h-3.5" />
                            {formatTime(booking.created_at)}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 flex-wrap justify-end">
                      <span className="font-semibold text-slate-800 ml-2">{booking.total_amount?.toFixed(2)} ر.س</span>

                      {/* Print Barcodes Button */}
                      <button
                        onClick={() => handlePrintBarcodes(booking)}
                        className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-all"
                        title="طباعة الباركودات"
                      >
                        <Printer className="w-4 h-4" />
                      </button>

                      {/* View Results — shown ONLY after the lab releases results (internal booking = completed) */}
                      {booking.results_ready && (
                        <button
                          onClick={() => navigate(`/contractor/report/${booking.internal_booking_id}`)}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm font-medium transition-all whitespace-nowrap"
                          title="عرض النتائج"
                        >
                          <FileBarChart2 className="w-4 h-4" />
                          <span className="hidden sm:inline">عرض النتائج</span>
                          <span className="sm:hidden">النتائج</span>
                        </button>
                      )}

                      {/* Mark as Collected (only if pending) */}
                      {booking.status === 'pending' && (
                        <button
                          onClick={() => handleUpdateStatus(booking._id, 'collected')}
                          disabled={updatingStatus}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 text-sm font-medium transition-all disabled:opacity-50 whitespace-nowrap"
                          title="تم جمع العينات"
                        >
                          <Package className="w-4 h-4" />
                          <span className="hidden sm:inline">تم الجمع</span>
                        </button>
                      )}
                      
                      <button
                        onClick={async () => {
                          // Always refetch the full booking on open so the tests table is populated
                          // (the list endpoint may return a lighter projection).
                          setSelectedBooking(booking);
                          setShowViewModal(true);
                          try {
                            const { data } = await contractorLabsAPI.getBookingById(booking._id);
                            const fresh = data?.data;
                            if (fresh) setSelectedBooking(fresh);
                          } catch (_) { /* keep optimistic data */ }
                        }}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium transition-all whitespace-nowrap"
                      >
                        <Eye className="w-4 h-4" />
                        <span className="hidden sm:inline">التفاصيل</span>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>

      {/* New Order Modal */}
      <AnimatePresence>
        {showNewOrderModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowNewOrderModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl shadow-xl w-[95vw] max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
            >
              {/* Header */}
              <div className="px-6 py-4 border-b border-gray-100 bg-[#1E293B]">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-bold text-white flex items-center gap-2">
                    <Plus className="w-5 h-5" />
                    طلب تحليل جديد
                  </h2>
                  <button onClick={() => setShowNewOrderModal(false)} className="text-white/70 hover:text-white transition-colors">
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="p-4 md:p-6 overflow-y-auto flex-1">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
                  {/* Patient Info */}
                  <div className="space-y-4">
                    <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                      <User className="w-5 h-5 text-blue-600" />
                      بيانات المريض
                    </h3>
                    
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">
                        اسم المريض <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={orderForm.patient_name}
                        onChange={(e) => setOrderForm({...orderForm, patient_name: e.target.value})}
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all"
                        placeholder="الاسم الكامل"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">
                        رقم الهوية <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={orderForm.patient_national_id}
                        onChange={(e) => setOrderForm({...orderForm, patient_national_id: e.target.value})}
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all"
                        placeholder="1xxxxxxxxx"
                        dir="ltr"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1.5">
                          تاريخ الميلاد <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="date"
                          value={orderForm.patient_dob}
                          onChange={(e) => setOrderForm({...orderForm, patient_dob: e.target.value})}
                          className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1.5">
                          الجنس <span className="text-red-500">*</span>
                        </label>
                        <select
                          value={orderForm.patient_gender}
                          onChange={(e) => setOrderForm({...orderForm, patient_gender: e.target.value})}
                          className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all bg-white"
                        >
                          <option value="Male">ذكر</option>
                          <option value="Female">أنثى</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">رقم الجوال</label>
                      <input
                        type="tel"
                        value={orderForm.patient_phone}
                        onChange={(e) => setOrderForm({...orderForm, patient_phone: e.target.value})}
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all"
                        placeholder="05xxxxxxxx"
                        dir="ltr"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">ملاحظات</label>
                      <textarea
                        value={orderForm.notes}
                        onChange={(e) => setOrderForm({...orderForm, notes: e.target.value})}
                        rows={3}
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all resize-none"
                        placeholder="ملاحظات إضافية..."
                      />
                    </div>
                  </div>

                  {/* Tests Selection */}
                  <div className="space-y-4">
                    <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                      <FlaskConical className="w-5 h-5 text-blue-600" />
                      اختيار التحاليل
                      <span className="text-xs text-gray-500 font-normal">(أسعار المختبرات المتعاقدة)</span>
                    </h3>

                    <div className="relative">
                      <input
                        type="text"
                        value={testSearchQuery}
                        onChange={(e) => setTestSearchQuery(e.target.value)}
                        placeholder="البحث عن تحليل..."
                        className="w-full px-4 py-2.5 pr-11 border border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all"
                      />
                      <Search className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    </div>

                    {/* Tests List */}
                    <div className="border border-gray-200 rounded-xl max-h-60 overflow-y-auto">
                      {loadingTests ? (
                        <div className="flex items-center justify-center py-8">
                          <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                        </div>
                      ) : filteredTests.length === 0 ? (
                        <div className="text-center py-8">
                          <AlertCircle className="w-8 h-8 text-amber-500 mx-auto mb-2" />
                          <p className="text-gray-500 text-sm">لا توجد تحاليل متاحة بأسعار المختبرات المتعاقدة</p>
                          <p className="text-gray-400 text-xs mt-1">تواصل مع إدارة المختبر لإضافة الأسعار</p>
                        </div>
                      ) : (
                        filteredTests.slice(0, 50).map(test => {
                          const isSelected = selectedTests.find(t => t._id === test._id);
                          
                          return (
                            <div
                              key={test._id}
                              onClick={() => toggleTest(test)}
                              className={`flex items-center justify-between gap-2 px-3 md:px-4 py-3 border-b border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors ${
                                isSelected ? 'bg-blue-50' : ''
                              }`}
                            >
                              <div className="flex items-center gap-3 min-w-0">
                                <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all flex-shrink-0 ${
                                  isSelected ? 'bg-blue-600 border-blue-600' : 'border-gray-300'
                                }`}>
                                  {isSelected && <CheckCircle className="w-3 h-3 text-white" />}
                                </div>
                                <div className="min-w-0">
                                  <p className="font-medium text-slate-800 truncate">{test.name_ar || test.name_en}</p>
                                  {test.name_ar && test.name_en && <p className="text-xs text-slate-500 truncate">{test.name_en}</p>}
                                </div>
                              </div>
                              <span className="font-semibold text-blue-600 text-sm whitespace-nowrap flex-shrink-0">{test.contracted_price?.toFixed(2)} ر.س</span>
                            </div>
                          );
                        })
                      )}
                    </div>

                    {/* Selected Tests */}
                    {selectedTests.length > 0 && (
                      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                        <div className="flex items-center justify-between mb-3">
                          <span className="font-medium text-blue-800">التحاليل المختارة ({selectedTests.length})</span>
                          <span className="font-bold text-blue-800">{totalAmount.toFixed(2)} ر.س</span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {selectedTests.map(test => (
                            <span
                              key={test._id}
                              className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-white rounded-lg text-xs md:text-sm border border-blue-200 max-w-full"
                            >
                              <span className="truncate">{test.name_ar || test.name_en}</span>
                              <button
                                onClick={(e) => { e.stopPropagation(); toggleTest(test); }}
                                className="text-red-500 hover:text-red-700 flex-shrink-0"
                              >
                                <X className="w-3.5 h-3.5" />
                              </button>
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="px-4 md:px-6 py-3 md:py-4 border-t border-gray-100 bg-gray-50 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
                <div className="text-center sm:text-right">
                  <span className="text-slate-600 text-sm">الإجمالي: </span>
                  <span className="text-lg md:text-xl font-bold text-blue-600">{totalAmount.toFixed(2)} ر.س</span>
                </div>
                <div className="flex gap-2 justify-end">
                  <button
                    onClick={() => setShowNewOrderModal(false)}
                    className="flex-1 sm:flex-none px-4 py-2.5 border border-gray-200 text-gray-600 rounded-xl hover:bg-gray-100 transition-all text-sm"
                  >
                    إلغاء
                  </button>
                  <button
                    onClick={handleSubmitOrder}
                    disabled={submitting || selectedTests.length === 0}
                    className="flex-1 sm:flex-none px-4 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-all text-sm whitespace-nowrap"
                  >
                    {submitting ? (<Loader2 className="w-4 h-4 animate-spin" />) : (<Plus className="w-4 h-4" />)}
                    إنشاء الطلب
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Success Modal with Barcodes */}
      <AnimatePresence>
        {showSuccessModal && createdBooking && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowSuccessModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col"
            >
              {/* Header */}
              <div className="px-6 py-4 border-b border-gray-100 bg-green-600">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-bold text-white flex items-center gap-2">
                    <CheckCircle className="w-5 h-5" />
                    تم إنشاء الطلب بنجاح!
                  </h2>
                  <button onClick={() => setShowSuccessModal(false)} className="text-white/70 hover:text-white transition-colors">
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="p-6 overflow-y-auto flex-1">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <CheckCircle className="w-8 h-8 text-green-600" />
                  </div>
                  <p className="text-slate-600">رقم الطلب</p>
                  <p className="text-2xl font-bold text-slate-800">#{createdBooking.booking_no}</p>
                </div>

                {/* Barcodes */}
                <div className="space-y-3">
                  <h4 className="font-semibold text-slate-800 flex items-center gap-2">
                    <Barcode className="w-5 h-5 text-blue-600" />
                    الباركودات
                  </h4>
                  <div className="bg-gray-50 rounded-xl p-4 space-y-2">
                    {(() => {
                      // Group tests by shared barcode so one physical sample = one barcode card
                      // showing all the tests it covers (like professional labs do).
                      const groups = new Map();
                      (createdBooking.tests || []).forEach((t) => {
                        const key = t.barcode || `__${Math.random()}`;
                        if (!groups.has(key)) groups.set(key, { barcode: t.barcode, tests: [] });
                        groups.get(key).tests.push(t);
                      });
                      return Array.from(groups.values()).map((g, idx) => (
                        <div key={g.barcode || idx} className="bg-white p-3 rounded-lg border border-gray-200">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2 text-xs text-slate-500">
                              <span className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full font-medium">
                                {g.tests.length} {g.tests.length === 1 ? 'تحليل' : 'تحاليل'}
                              </span>
                            </div>
                            <div className="flex items-center gap-2 bg-gray-100 px-3 py-1.5 rounded-lg">
                              <Barcode className="w-4 h-4 text-gray-500" />
                              <span className="font-mono text-sm font-bold text-slate-800">{g.barcode}</span>
                            </div>
                          </div>
                          <ul className="space-y-0.5 pr-2">
                            {g.tests.map((t, ti) => (
                              <li key={ti} className="text-sm text-slate-700">
                                • {t.lab_test_id?.name_ar || t.lab_test_id?.name_en || 'تحليل'}
                                {t.lab_test_id?.name_ar && t.lab_test_id?.name_en && (
                                  <span className="text-xs text-slate-400 ms-2">{t.lab_test_id.name_en}</span>
                                )}
                              </li>
                            ))}
                          </ul>
                        </div>
                      ));
                    })()}
                  </div>
                </div>

                <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-xl">
                  <p className="text-sm text-amber-700">
                    <strong>تنبيه:</strong> يرجى طباعة الباركودات وإلصاقها على العينات قبل إرسالها للمختبر.
                  </p>
                </div>
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex justify-between gap-3">
                <button
                  onClick={() => setShowSuccessModal(false)}
                  className="px-5 py-2.5 border border-gray-200 text-gray-600 rounded-xl hover:bg-gray-100 transition-all"
                >
                  إغلاق
                </button>
                <button
                  onClick={() => handlePrintBarcodes(createdBooking)}
                  className="px-5 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 flex items-center gap-2 transition-all"
                >
                  <Printer className="w-5 h-5" />
                  طباعة الباركودات
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* View Booking Modal */}
      <AnimatePresence>
        {showViewModal && selectedBooking && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowViewModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl shadow-xl w-[95vw] max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
            >
              {/* Header */}
              <div className="px-6 py-4 border-b border-gray-100 bg-[#1E293B]">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-bold text-white">
                    تفاصيل الطلب #{selectedBooking.booking_no}
                  </h2>
                  <button onClick={() => setShowViewModal(false)} className="text-white/70 hover:text-white transition-colors">
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="p-6 overflow-y-auto flex-1 space-y-6">
                {/* Status & Actions */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-slate-600">الحالة:</span>
                    {(() => {
                      const status = getStatusInfo(selectedBooking.status);
                      const StatusIcon = status.icon;
                      return (
                        <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium ${status.color}`}>
                          <StatusIcon className="w-4 h-4" />
                          {status.label}
                        </span>
                      );
                    })()}
                  </div>
                  
                  {/* Status Change Button */}
                  {selectedBooking.status === 'pending' && (
                    <button
                      onClick={() => handleUpdateStatus(selectedBooking._id, 'collected')}
                      disabled={updatingStatus}
                      className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium transition-all disabled:opacity-50"
                    >
                      {updatingStatus ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Package className="w-4 h-4" />
                      )}
                      تأكيد جمع العينات
                    </button>
                  )}
                </div>

                {/* Patient Info */}
                <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                  <h4 className="font-semibold text-slate-800">بيانات المريض</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-slate-500">الاسم:</span>
                      <p className="font-medium text-slate-800">{selectedBooking.patient_name}</p>
                    </div>
                    <div>
                      <span className="text-slate-500">رقم الهوية:</span>
                      <p className="font-medium text-slate-800" dir="ltr">{selectedBooking.patient_national_id}</p>
                    </div>
                    <div>
                      <span className="text-slate-500">الجنس:</span>
                      <p className="font-medium text-slate-800">{selectedBooking.patient_gender === 'Male' ? 'ذكر' : 'أنثى'}</p>
                    </div>
                    <div>
                      <span className="text-slate-500">تاريخ الميلاد:</span>
                      <p className="font-medium text-slate-800">{formatDate(selectedBooking.patient_dob)}</p>
                    </div>
                  </div>
                </div>

                {/* Tests with Barcodes — grouped by shared sample (tests using the same tube share ONE barcode) */}
                <div>
                  <h4 className="font-semibold text-slate-800 mb-3">العيّنات والتحاليل</h4>
                  <div className="space-y-2">
                    {(() => {
                      const groups = new Map();
                      (selectedBooking.tests || []).forEach((t) => {
                        const key = t.barcode || `__${Math.random()}`;
                        if (!groups.has(key)) groups.set(key, { barcode: t.barcode, tests: [], total: 0 });
                        const g = groups.get(key);
                        g.tests.push(t);
                        g.total += Number(t.price || 0);
                      });
                      return Array.from(groups.values()).map((g, idx) => (
                        <div key={g.barcode || idx} className="border border-gray-200 rounded-xl p-3 bg-white">
                          <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                            <span className="bg-blue-50 text-blue-700 px-2.5 py-0.5 rounded-full text-xs font-medium">
                              {g.tests.length} {g.tests.length === 1 ? 'تحليل' : 'تحاليل'}
                            </span>
                            <div className="flex items-center gap-2 bg-gray-100 px-3 py-1.5 rounded-lg">
                              <Barcode className="w-4 h-4 text-slate-400" />
                              <span className="font-mono text-sm font-bold text-slate-800">{g.barcode}</span>
                            </div>
                          </div>
                          <ul className="divide-y divide-gray-100">
                            {g.tests.map((t, ti) => (
                              <li key={ti} className="py-1.5 flex items-center justify-between gap-2">
                                <div className="min-w-0">
                                  <p className="text-sm font-medium text-slate-800 truncate">
                                    {t.lab_test_id?.name_ar || t.lab_test_id?.name_en || 'تحليل'}
                                  </p>
                                  {t.lab_test_id?.name_ar && t.lab_test_id?.name_en && (
                                    <p className="text-xs text-slate-400 truncate">{t.lab_test_id.name_en}</p>
                                  )}
                                </div>
                                <span className="text-sm text-slate-600 flex-shrink-0">{Number(t.price || 0).toFixed(2)} ر.س</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ));
                    })()}
                  </div>
                  <div className="mt-3 bg-blue-50 rounded-lg px-4 py-2.5 flex items-center justify-between">
                    <span className="font-semibold text-slate-800">الإجمالي</span>
                    <span className="font-bold text-blue-600">{selectedBooking.total_amount?.toFixed(2)} ر.س</span>
                  </div>
                </div>

                {/* Notes */}
                {selectedBooking.notes && (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                    <h4 className="font-semibold text-amber-800 mb-2">ملاحظات</h4>
                    <p className="text-amber-700">{selectedBooking.notes}</p>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex justify-between">
                <button
                  onClick={() => handlePrintBarcodes(selectedBooking)}
                  className="px-5 py-2.5 border border-gray-200 text-gray-700 rounded-xl hover:bg-gray-100 flex items-center gap-2 transition-all"
                >
                  <Printer className="w-5 h-5" />
                  طباعة الباركودات
                </button>
                <button
                  onClick={() => setShowViewModal(false)}
                  className="px-5 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-all"
                >
                  إغلاق
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ContractorDashboard;
